import FaIcon from '@app/lui3/FaIcon';
import {useCallback} from 'react';
import styled from 'styled-components';

import {fetchOutlookDealAutocomplete, OutlookDealJson} from '../../requests';
import {usePodRequest} from '../../usePodRequest';
import EntityContainer from './EntityContainer';
import EntityLink from './EntityLink';
import LinkSelector from './LinkSelector';
import {DealLink, MessageAttachment} from './structs';

interface Props {
  agencyId: number;
  dealLinks: DealLink[];
  setDealLinks: (dealLinks: DealLink[]) => void;
  attachments: MessageAttachment[];
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DealLinks = ({agencyId, dealLinks, setDealLinks, attachments}: Props) => {
  const {request} = usePodRequest();

  const linkedDealIds = new Set(dealLinks.map((link) => link.deal.id));

  const searchDeals = useCallback(
    async (query: string) => {
      const {deals} = await fetchOutlookDealAutocomplete(request, agencyId, {
        query,
      });
      return deals;
    },
    [agencyId, request]
  );

  const handleSelect = (deal: OutlookDealJson) => {
    if (linkedDealIds.has(deal.id)) {
      return;
    }
    setDealLinks([...dealLinks, {deal, attachmentIds: []}]);
  };

  const handleRemove = (dealId: number) => {
    setDealLinks(dealLinks.filter((link) => link.deal.id !== dealId));
  };

  const handleAttachmentsChanged = (
    dealId: number,
    attachmentIds: string[]
  ) => {
    setDealLinks(
      dealLinks.map((link) =>
        link.deal.id === dealId ? {...link, attachmentIds} : link
      )
    );
  };

  return (
    <Container>
      <LinkSelector
        title="Deals"
        placeholder="Add Deals..."
        searchOptions={searchDeals}
        getOptionKey={(deal) => deal.id}
        renderOption={(deal) => deal.name ?? ''}
        onSelect={handleSelect}
        excludeKeys={linkedDealIds}
      />
      {dealLinks.length > 0 && (
        <EntityContainer>
          {dealLinks.map((link) => (
            <EntityLink
              avatar={
                <FaIcon icon="circle-dollar" faStyle="solid" color="gray-600" />
              }
              key={link.deal.id}
              name={link.deal.name ?? ''}
              attachmentIds={link.attachmentIds}
              allAttachments={attachments}
              showPinned={false}
              pinned={false}
              attachmentType="Document"
              togglePinned={() => undefined}
              remove={() => handleRemove(link.deal.id)}
              onAttachmentsChanged={(attachmentIds: string[]) =>
                handleAttachmentsChanged(link.deal.id, attachmentIds)
              }
            />
          ))}
        </EntityContainer>
      )}
    </Container>
  );
};

export default DealLinks;
